import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import GameBoard from "./components/GameBoard";
import Chat from "./components/Chat";
import Auth from "./components/Auth";
import AuthCallback from "./components/AuthCallback";
import MyGames from "./components/MyGames";
import Profile from "./components/Profile";
import MagicPage from "./components/MagicPage";
import LillaLandingPage from "./components/LillaLandingPage";
import PaymentSuccess from "./components/PaymentSuccess";
import GuestGamePage from "./components/GuestGamePage";
import DiceAnimationOverlay from "./components/DiceAnimationOverlay";
import AffiliateRedirect from "./components/AffiliateRedirect";
import AffiliateDashboard from "./components/AffiliateDashboard";
import AdminAffiliates from "./components/AdminAffiliates";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { GameAnimationProvider, useGameAnimation } from "./components/GameAnimationManager";
import { createGame, getAuthStatus, getGameData } from "./api";
import config from "./config";
import "./App.css";

// RedTrack rt_clickid автосохранение
function useRtClickId() {
  useEffect(() => {
    // Проверяем URL параметры при загрузке любой страницы
    const urlParams = new URLSearchParams(window.location.search);
    const rtClickId = urlParams.get('rt_clickid');

    if (rtClickId) {
      localStorage.setItem('rt_clickid', rtClickId);
      console.log('✅ rt_clickid автоматически сохранен:', rtClickId.substring(0,8));
    } else {
      // Проверяем, есть ли уже сохраненный rt_clickid
      const savedRtClickId = localStorage.getItem('rt_clickid');
      if (savedRtClickId) {
        console.log('✅ rt_clickid уже сохранен:', savedRtClickId.substring(0,8));
      }
    }
  }, []);
}

const SNAKES = [
  { from: 12, to: 8 },
  { from: 16, to: 4 },
  { from: 24, to: 7 },
  { from: 29, to: 6 },
  { from: 44, to: 9 },
  { from: 52, to: 35 },
  { from: 55, to: 3 },
  { from: 61, to: 13 },
  { from: 63, to: 2 },
  { from: 72, to: 51 },
];

const LADDERS = [
  { from: 10, to: 23 },
  { from: 17, to: 69 },
  { from: 20, to: 32 },
  { from: 22, to: 60 },
  { from: 27, to: 41 },
  { from: 28, to: 50 },
  { from: 37, to: 66 },
  { from: 45, to: 67 },
  { from: 46, to: 62 },
  { from: 54, to: 68 },
];

// Компонент навигации
function Navigation() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return null;

  return (
    <nav className="lila-header">
      {/* Burger menu слева */}
      <div className="position-relative">
        <button
          className="btn btn-link text-white p-0 border-0 text-decoration-none"
          style={{ fontSize: '24px' }}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Меню"
        >
          ☰<span className="d-none d-md-inline ms-2" style={{ fontSize: '16px' }}>Меню</span>
        </button>

        {/* Выпадающее меню */}
        {menuOpen && (
          <div
            className="dropdown-menu show position-absolute"
            style={{ top: '100%', left: 0, marginTop: '8px', minWidth: '180px' }}
          >
            <Link
              className="dropdown-item"
              to="/my-games"
              onClick={() => setMenuOpen(false)}
            >
              Мои игры
            </Link>
            <Link
              className="dropdown-item"
              to="/profile"
              onClick={() => setMenuOpen(false)}
            >
              Профиль
            </Link>
            <div className="dropdown-divider"></div>
            <button
              className="dropdown-item"
              onClick={() => {
                setMenuOpen(false);
                signOut();
              }}
            >
              Выйти
            </button>
          </div>
        )}
      </div>

      {/* Центрированное название */}
      <Link
        to="/my-games"
        className="text-white text-decoration-none fw-bold"
        style={{ fontSize: '18px', position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
      >
        Лила Онлайн
      </Link>

      {/* Пустой div для баланса */}
      <div style={{ width: '24px' }}></div>
    </nav>
  );
}

// Главный компонент игры
function GameApp() {
  const { user, loading, signOut, isAuthRequired, isAuthAvailable } = useAuth();
  const { animationState } = useGameAnimation();

  // Автоматическое сохранение rt_clickid из URL
  useRtClickId();
  const [gameId, setGameId] = useState<number | null>(null);
  const [currentCell, setCurrentCell] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authStatus, setAuthStatus] = useState<any>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Проверяем URL параметры для загрузки существующей игры
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gameIdParam = urlParams.get('gameId');
    if (gameIdParam && !isNaN(Number(gameIdParam))) {
      const id = Number(gameIdParam);
      setGameId(id);
      // Игру загрузим только после авторизации
    }
  }, []);

  // Получаем статус авторизации при загрузке
  useEffect(() => {
    const fetchAuthStatus = async () => {
      try {
        const status = await getAuthStatus();
        setAuthStatus(status);
      } catch (error) {
        console.error('Error fetching auth status:', error);
      }
    };

    fetchAuthStatus();
  }, []);

  // Загружаем игру после авторизации (если есть gameId в URL)
  useEffect(() => {
    if (gameId && user && authStatus) {
      loadGameData(gameId);
    }
  }, [gameId, user, authStatus]);

  // Проверяем, нужна ли авторизация (всегда требуем авторизацию если auth включена)
  const needsAuth = authStatus?.auth_enabled && !user;

  // Функция для загрузки данных игры
  const loadGameData = async (gameId: number) => {
    try {
      setAccessError(null); // Сбрасываем ошибку доступа
      console.log('Loading game data for gameId:', gameId);
      const gameData = await getGameData(gameId);
      console.log('Game data loaded:', gameData);
      setCurrentCell(gameData.current_cell);
      setPlayerName(gameData.player_name);
      setMessage(`Игра #${gameId} загружена. Позиция: ${gameData.current_cell}`);
    } catch (error: any) {
      console.error('Error loading game data:', error);
      if (error.status === 403) {
        setAccessError('Нет доступа к этой игре. Игра доступна только её владельцу.');
        setMessage(''); // Очищаем обычное сообщение
      } else if (error.status === 404) {
        setAccessError('Игра не найдена.');
        setMessage('');
      } else {
        setAccessError('Ошибка загрузки игры');
        setMessage('');
      }
    }
  };

  async function handleCreateGame() {
    // Используем общую логику из handleCreateGameClick
    handleCreateGameClick();
  }

  async function handleCreateGameSubmit() {
    console.log("🔥 handleCreateGameSubmit вызвана!");
    console.log("playerName:", playerName);

    if (!playerName.trim()) {
      setMessage("Введите имя для начала пути");
      return;
    }

    // Закрываем модальное окно
    setShowCreateModal(false);

    // Редиректим на страницу магии
    const encodedPlayerName = encodeURIComponent(playerName.trim());
    window.location.href = `/magic?playerName=${encodedPlayerName}&redirectTo=${encodeURIComponent('/')}`;
  }


  const handleCreateGameClick = () => {
    // Если пользователь не авторизован - показываем форму авторизации
    if (needsAuth) {
      setShowAuth(true);
      return;
    }
    
    // Если пользователь авторизован - показываем модальное окно создания игры
    // Получаем имя пользователя из профиля для предзаполнения
    const profileName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';
    setPlayerName(profileName);
    setShowCreateModal(true);
  };

  const handleUseProfileName = () => {
    const profileName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';
    setPlayerName(profileName);
  };

  function handleCellChange(newCell: number) {
    setCurrentCell(newCell);
  }

  // Показываем загрузку пока проверяем авторизацию
  if (loading || !authStatus) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <p className="text-muted">Загружаем приложение...</p>
        </div>
      </div>
    );
  }

  // Показываем форму авторизации
  if (showAuth && isAuthAvailable) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center">
        <div className="row justify-content-center w-100">
          <div className="col-md-6 col-lg-4">
            <Auth onClose={() => setShowAuth(false)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>

      {!gameId ? (
        <div className="hero-container">
          {/* Героевая секция */}
          <div className="hero-section">
            <div className="container">
              {/* Блок статуса авторизации */}
              {isAuthAvailable && !user && authStatus?.auth_enabled && (
                <div className="text-end mb-3">
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowAuth(true)}
                  >
                    Войти
                  </button>
                </div>
              )}
          {/* Логотип и основной заголовок */}
          <div className="hero-header text-center mb-5">
            <div className="hero-logo mb-3">
              <span className="quantum-symbol">⚛️</span>
            </div>
            <h1 className="hero-title mb-3">
              Квантовый Перекрёсток
            </h1>
            <h2 className="hero-subtitle mb-4">
              Научно обоснованная система трансформации сознания
            </h2>
            
            {/* Ключевые метрики */}
            <div className="hero-metrics d-flex justify-content-center flex-wrap gap-4 mb-4">
              <div className="metric-item">
                <span className="metric-number">72</span>
                <span className="metric-label">уровня развития</span>
              </div>
              <div className="metric-item">
                <span className="metric-number">Sonnet 4.5</span>
                <span className="metric-label">ИИ-наставник</span>
              </div>
              <div className="metric-item">
                <span className="metric-number">∞</span>
                <span className="metric-label">персональных путей</span>
              </div>
            </div>
          </div>

          {/* Блок ценностного предложения */}
          <div className="value-proposition mb-5">
            <div className="row">
              <div className="col-lg-8 mx-auto">
                <div className="value-card">
                  <h3 className="value-title mb-4">
                    🎯 Что делает систему уникальной
                  </h3>
                  
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <div className="feature-item">
                        <div className="feature-icon">🧠</div>
                        <div className="feature-content">
                          <h4>ИИ-анализ Claude 4.5</h4>
                          <p>Персональная адаптация каждого диалога на основе ваших ответов и эмоционального состояния</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6 mb-4">
                      <div className="feature-item">
                        <div className="feature-icon">📊</div>
                        <div className="feature-content">
                          <h4>Научная методология</h4>
                          <p>Основана на книге Сергея Будха с интеграцией современной психологии трансформации</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6 mb-4">
                      <div className="feature-item">
                        <div className="feature-icon">🎮</div>
                        <div className="feature-content">
                          <h4>Игровая механика</h4>
                          <p>72 уникальные ситуации с мгновенной обратной связью и адаптивным развитием сюжета</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6 mb-4">
                      <div className="feature-item">
                        <div className="feature-icon">🔐</div>
                        <div className="feature-content">
                          <h4>Конфиденциальность</h4>
                          <p>Ваши данные остаются анонимными, ИИ работает в безопасной изолированной среде</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="cta-section text-center mb-5">
            <div className="cta-card">
              <h3 className="cta-title mb-3">
                ⚡ Готовы начать трансформацию?
              </h3>
                             <p className="cta-description mb-4">
                 Введите своё имя, и ваш персональный ведущий создаст путь развития, 
                 адаптированный именно под ваши потребности и цели.
               </p>
            </div>
          </div>

          {!gameId && (
            <>
              {/* Форма входа */}
              <div className="entry-form-container">
                <div className="row justify-content-center">
                  <div className="col-md-6 col-lg-4">
                    <div className="entry-form">

                      <button
                        className={`btn btn-primary btn-lg w-100 hero-cta-button ${isCreating ? "magic-animation" : ""}`}
                        onClick={handleCreateGameClick}
                        disabled={isCreating}
                      >
                        {isCreating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Вселенная создаёт ваш путь...
                          </>
                        ) : needsAuth ? (
                          <>
                            🔐 Войти и начать путь
                          </>
                        ) : (
                          <>
                            🚀 Начать путь трансформации
                          </>
                        )}
                      </button>
                      {message && (
                        <div className={`mt-3 alert ${message.includes('Ошибка') ? 'alert-danger' : 'alert-info'}`}>
                          {message}
                        </div>
                      )}
                      {accessError && (
                        <div className="mt-3 alert alert-danger">
                          {accessError}
                          <div className="mt-2">
                            <a href="/my-games" className="btn btn-sm btn-outline-primary me-2">
                              Мои игры
                            </a>
                            <button 
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                setAccessError(null);
                                setGameId(null);
                                window.history.replaceState({}, '', '/');
                              }}
                            >
                              На главную
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Доверие и доказательства */}
              <div className="trust-section mt-5 pt-5 border-top">
                <div className="row">
                  <div className="col-lg-8 mx-auto text-center">
                    <h4 className="trust-title mb-4">
                      🕉️ Создаём новую реальность на основе науки
                    </h4>
                    
                    <div className="trust-content">
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <div className="trust-item">
                            <strong>Автор методологии</strong>
                            <p className="small text-muted">Сергей Будха, книга "Квантовый перекрёсток"</p>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="trust-item">
                            <strong>ИИ-технологии</strong>
                            <p className="small text-muted">Anthropic Claude 4.5 для персонализации</p>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="trust-item">
                            <strong>Разработка</strong>
                            <p className="small text-muted">
                              <a href="https://t.me/AlexGo007" target="_blank" rel="noreferrer" className="text-decoration-none">
                                Связаться с создателем
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="trust-quote mt-4">
                      <p className="font-italic text-muted">
                        "Первый шаг к своей истинной природе начинается с осознанного выбора. 
                        Квантовый Перекрёсток — это ваш проводник в этом путешествии."
                      </p>
                      <small className="text-muted">© Прогресс сохраняется в вашем аккаунте</small>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="container mt-3">
      <div className="row">
        <div className="col-12 text-center mb-4">
          <div className="alert alert-info">
            <h5 className="mb-3">🎮 Игра началась!</h5>

            <div className="text-start mb-3">
              <p className="mb-1">1️⃣ Поле сверху — наблюдай за своим путём.</p>
              <p className="mb-1">2️⃣ Внизу — <a href="#chat-section" className="alert-link">чат с Ведущим</a>, туда пиши свой запрос.</p>
              <p className="mb-0">3️⃣ Если что-то непонятно — я рядом, пиши мне в <a
                href="https://t.me/AlexGo007"
                target="_blank"
                rel="noreferrer"
                className="alert-link"
              >Telegram</a>.</p>
            </div>

            <div className="accordion" id="gameInstructionsAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingInstructions">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseInstructions"
                    aria-expanded="false"
                    aria-controls="collapseInstructions"
                  >
                    ℹ️ Как играть?
                  </button>
                </h2>
                <div
                  id="collapseInstructions"
                  className="accordion-collapse collapse"
                  aria-labelledby="headingInstructions"
                  data-bs-parent="#gameInstructionsAccordion"
                >
                  <div className="accordion-body text-start">
                    <ul className="mb-3">
                      <li>💬 Весь игровой процесс идёт через чат с Ведущим — поле лишь отражает ваш путь.</li>
                      <li>🎲 Кубик бросается в чате (появится кнопка, когда придёт время).</li>
                      <li>🗺️ Поле всегда сверху страницы, чтобы вы могли наблюдать движение.</li>
                      <li>🎯 Цель игры — раскрыть свой запрос и увидеть, как поле отражает твой путь.</li>
                    </ul>

                    <p className="mb-2"><strong>💡 Как составить запрос:</strong></p>
                    <ul className="mb-3">
                      <li>От конкретной темы — «Отношения», «Деньги», «Самореализация»</li>
                      <li>От чувства или боли — «Боюсь проявляться», «Чувствую застой»</li>
                      <li>С целью — «Запустить свой проект на тему...»</li>
                    </ul>

                    <p className="mb-2"><strong>❌ Не подходит:</strong></p>
                    <p className="mb-0">«Быть счастливым», «Изменить всё сразу»</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )}

      {currentCell !== null && (
        <GameBoard
          currentCell={currentCell}
          snakes={SNAKES}
          ladders={LADDERS}
          special={{
            startFinish: 68,
            checks: [69, 70, 71]
          }}
          onCellClick={(cell) => {
            console.log(`Clicked cell ${cell}`);
          }}
        />
      )}

      {gameId && (
        <div id="chat-section" className="mt-4">
          <Chat gameId={gameId} onCellChange={handleCellChange} />
        </div>
      )}

      {/* Оверлей анимации кубика */}
      <DiceAnimationOverlay
        isVisible={animationState.isDiceRolling}
        stage={animationState.currentStage}
        diceResult={animationState.diceResult}
        onComplete={() => {}}
      />

      {/* Модальное окно создания игры */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Создать новую игру</h3>
            <p>Введите имя игрока:</p>
            
            <div className="name-input-section">
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Введите имя игрока"
                className="name-input"
                maxLength={50}
              />
              
              {user && (user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email) && (
                <button 
                  type="button"
                  onClick={handleUseProfileName}
                  className="use-profile-btn"
                >
                  Использовать имя из профиля
                </button>
              )}
            </div>

            <div className="modal-actions">
              <button 
                onClick={() => setShowCreateModal(false)}
                className="cancel-btn"
                disabled={isCreating}
              >
                Отмена
              </button>
              <button 
                onClick={handleCreateGameSubmit}
                className="create-btn"
                disabled={isCreating || !playerName.trim()}
              >
                {isCreating ? 'Создание...' : 'Создать игру'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer - скрываем на странице игры */}
      {!gameId && (
        <footer className="mt-5 pt-4 border-top">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center">
                <p className="text-muted mb-2">
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a> · 
                  <a href="/cookies.html" target="_blank" rel="noopener noreferrer">Cookies</a> · 
                  <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a> · 
                  <a href="/contact.html" target="_blank" rel="noopener noreferrer">Contact</a> · 
                  <a href="/impressum.html" target="_blank" rel="noopener noreferrer">Impressum</a>
                </p>
              </div>
            </div>
          </div>
        </footer>
      )}
    </>
  );
}

// Основная функция App с роутингом и провайдерами
function App() {
  return (
    <AuthProvider>
      <GameAnimationProvider>
        <Router>
          <Navigation />
          <Routes>
            <Route path="/" element={<GameApp />} />
            <Route path="/go/:code" element={<AffiliateRedirect />} />
            <Route path="/try" element={<GuestGamePage />} />
            <Route path="/lilla" element={<LillaLandingPage />} />
            <Route path="/my-games" element={<MyGames />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
            <Route path="/admin/affiliates" element={<AdminAffiliates />} />
            <Route path="/magic" element={<MagicPage />} />
            <Route path="/auth" element={
              <div className="hero-container">
                <div className="hero-section">
                  <div className="container-fluid min-vh-100 d-flex align-items-start justify-content-center pt-5">
                    <div className="row justify-content-center w-100">
                      <div className="col-md-6 col-lg-4">
                        <Auth />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            } />
            <Route path="/auth-variant-b" element={
              <div className="hero-container">
                <div className="hero-section">
                  <div className="container-fluid min-vh-100 d-flex align-items-start justify-content-center pt-1">
                    <div className="row justify-content-center w-100">
                      <div className="col-md-6 col-lg-4">
                        <Auth variant="b" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            } />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
          </Routes>
        </Router>
      </GameAnimationProvider>
    </AuthProvider>
  );
}

export default App;